import { k8sRequest, makeK8sCreator } from "../api";
import { formatAge } from "../format";
import { K8sDaemonSet, K8sDaemonSetList, K8sPodTemplateSpec, RolloutState, WorkloadDetail, WorkloadRow } from "./types";

/** Kubernetes sets this on every node; as a target it means "one specific node". */
const HOSTNAME_LABEL = "kubernetes.io/hostname";

/**
 * Describe where a workload is targeted, in words rather than as a selector.
 *
 * The cluster uses two patterns today - a hostname selector for one node, and a
 * plain label such as hardware=rpi - and multi-node targeting needs
 * nodeAffinity, because nodeSelector is an AND of equality matches and cannot
 * express "one of these".
 */
export const describeTargeting = (template?: K8sPodTemplateSpec): string => {
    const spec = template || {};
    const selector = spec.nodeSelector || {};
    const keys = Object.keys(selector);

    const terms = ((spec.affinity || {}).nodeAffinity || {}).requiredDuringSchedulingIgnoredDuringExecution || {};
    const expressions = (terms.nodeSelectorTerms || []).reduce(
        (all, term) => all.concat(term.matchExpressions || []),
        [] as { key: string; operator: string; values?: string[] }[]
    );
    const hostnames = expressions.filter((e) => e.key === HOSTNAME_LABEL && e.operator === "In");

    if (hostnames.length) {
        const count = (hostnames[0].values || []).length;

        return count === 1 ? "1 specific node" : `${count} specific nodes`;
    }

    if (!keys.length) {
        return expressions.length ? "Node affinity rules" : "All nodes";
    }

    if (keys.length === 1 && keys[0] === HOSTNAME_LABEL) {
        return "1 specific node";
    }

    return keys.map((key) => `${key}=${selector[key]}`).join(", ");
};

/**
 * A rollout's state. Kubernetes reports several counters and the useful reading
 * depends on which disagree:
 *
 *   - the controller has not observed the latest spec yet -> nothing is
 *     happening *yet*, which is otherwise indistinguishable from a stall
 *   - nothing is desired -> the selector matched no nodes, which reads as zero
 *     but is a targeting mistake rather than a failing rollout
 */
export const rolloutState = (daemonSet: K8sDaemonSet): RolloutState => {
    const status = daemonSet.status || {};
    const desired = status.desiredNumberScheduled || 0;
    const ready = status.numberReady || 0;
    const updated = status.updatedNumberScheduled || 0;
    const generation = daemonSet.metadata.generation;
    const observed = status.observedGeneration;

    if (generation !== undefined && observed !== undefined && observed < generation) {
        return "Pending";
    }
    if (desired === 0) {
        return "No matching nodes";
    }
    if (ready === desired) {
        return "Ready";
    }
    if (updated < desired) {
        return "In progress";
    }

    return "Not ready";
};

const firstContainerName = (daemonSet: K8sDaemonSet): string => {
    const containers = (((daemonSet.spec || {}).template || {}).spec || {}).containers || [];

    return containers.length ? containers[0].name || "" : "";
};

const firstImage = (daemonSet: K8sDaemonSet): string => {
    const containers = (((daemonSet.spec || {}).template || {}).spec || {}).containers || [];

    return containers.length ? containers[0].image || "" : "";
};

const toRow = (daemonSet: K8sDaemonSet): WorkloadRow => {
    const status = daemonSet.status || {};
    const namespace = daemonSet.metadata.namespace || "default";

    return {
        id: `${namespace}/${daemonSet.metadata.name}`,
        name: daemonSet.metadata.name,
        namespace,
        image: firstImage(daemonSet),
        state: rolloutState(daemonSet),
        ready: `${status.numberReady || 0}/${status.desiredNumberScheduled || 0}`,
        desired: status.desiredNumberScheduled || 0,
        updated: status.updatedNumberScheduled || 0,
        available: status.numberAvailable || 0,
        targeting: describeTargeting(((daemonSet.spec || {}).template || {}).spec),
        age: formatAge(daemonSet.metadata.creationTimestamp),
    };
};

/** Flatten a DaemonSetList response into grid rows. */
export const parseWorkloadList = (body: unknown): WorkloadRow[] => {
    const list = (body || {}) as K8sDaemonSetList;

    return (list.items || []).map(toRow);
};

/** "app=tetris" - the selector the workload stamps on its pods. */
export const describeSelector = (daemonSet: K8sDaemonSet): string => {
    const labels = ((daemonSet.spec || {}).selector || {}).matchLabels || {};

    return Object.keys(labels)
        .sort()
        .map((key) => `${key}=${labels[key]}`)
        .join(",");
};

/** Flatten a single DaemonSet into everything the detail Overview renders. */
export const parseWorkload = (body: unknown): WorkloadDetail | undefined => {
    const daemonSet = body as K8sDaemonSet | undefined;

    if (!daemonSet || !daemonSet.metadata || !daemonSet.metadata.name) {
        return undefined;
    }

    const status = daemonSet.status || {};
    const strategy = (daemonSet.spec || {}).updateStrategy || {};
    const generation = daemonSet.metadata.generation;
    const observed = status.observedGeneration;

    return {
        name: daemonSet.metadata.name,
        uid: daemonSet.metadata.uid || "",
        namespace: daemonSet.metadata.namespace || "default",
        image: firstImage(daemonSet),
        containerName: firstContainerName(daemonSet),
        state: rolloutState(daemonSet),
        targeting: describeTargeting(((daemonSet.spec || {}).template || {}).spec),
        age: formatAge(daemonSet.metadata.creationTimestamp),
        created: daemonSet.metadata.creationTimestamp || "",
        selector: describeSelector(daemonSet),
        updateStrategy: strategy.type || "",
        maxUnavailable: `${(strategy.rollingUpdate || {}).maxUnavailable ?? ""}`,
        desired: status.desiredNumberScheduled || 0,
        current: status.currentNumberScheduled || 0,
        ready: status.numberReady || 0,
        updated: status.updatedNumberScheduled || 0,
        available: status.numberAvailable || 0,
        unavailable: status.numberUnavailable || 0,
        misscheduled: status.numberMisscheduled || 0,
        awaitingObservation: generation !== undefined && observed !== undefined && observed < generation,
    };
};

const workloads = makeK8sCreator<WorkloadRow[]>("k8sWorkloads", parseWorkloadList);
const created = makeK8sCreator<WorkloadDetail | undefined>("k8sWorkloadCreate", parseWorkload);
const workload = makeK8sCreator<WorkloadDetail | undefined>("k8sWorkload", parseWorkload);

/** Namespace defaults to "default"; nothing on this cluster uses another one. */
const getWorkloadsAction = (namespace = "default") =>
    workloads.createThunk(k8sRequest(`apis/apps/v1/namespaces/${encodeURIComponent(namespace)}/daemonsets`));

const getWorkloadAction = (name: string, namespace = "default") =>
    workload.createThunk(
        k8sRequest(`apis/apps/v1/namespaces/${encodeURIComponent(namespace)}/daemonsets/${encodeURIComponent(name)}`)
    );

/**
 * Apply a new workload. The create call keeps its own reducer so a failure
 * surfaces on the wizard rather than blanking the list behind it.
 */
const createWorkloadAction = (manifest: K8sDaemonSet, namespace = "default") =>
    created.createThunk({
        ...k8sRequest(`apis/apps/v1/namespaces/${encodeURIComponent(namespace)}/daemonsets`),
        method: "POST",
        data: manifest,
    });

/**
 * Clear the create state. It is a global reducer that outlives the wizard, so
 * a previous deploy would otherwise still read as "succeeded" the next time
 * the wizard opens.
 */
const resetWorkloadCreateAction = () => created.createResetAction();

export { getWorkloadsAction, getWorkloadAction, createWorkloadAction, resetWorkloadCreateAction };
export const reducer = workloads.reducer;
export const workloadDetailReducer = workload.reducer;
export const workloadCreateReducer = created.reducer;
